/**
 * Plans panel — current plan card + upgrade/downgrade table.
 *
 * Reads ``snapshot.plans_available`` (already filtered to the right
 * scope by the backend) and lets the user one-click switch via
 * Stripe Checkout (POST /v1/billing/checkout-session). The "current
 * plan" gets a highlighted card; other plans render as side-by-side
 * comparison cards with the most relevant features (credits + key
 * quotas).
 *
 * If Stripe isn't configured in this environment the switch still
 * works: the backend applies the plan change immediately (assuming
 * the payment goes through) and an explanatory note renders above the
 * cards.
 */

import React, { useState } from 'react';
import { connect } from 'react-redux';
import {
  Box, Typography, Card, CardContent, Button, Stack, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  CircularProgress, Divider,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import StarIcon from '@mui/icons-material/Star';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

import { startCheckout, clearState as clearCheckoutState } from '../../redux/actions/billing/checkout';
import { tierLabel } from './billingHelpers';

// Tier ordering by "size" — used to label moves as upgrade vs downgrade.
const TIER_ORDER = ['free', 'starter', 'team', 'enterprise'];

const tierRank = (id) => {
  const idx = TIER_ORDER.indexOf((id || '').toLowerCase());
  return idx === -1 ? 999 : idx;
};

// ``deployments`` is a flat scalar cap - all deployments are treated
// the same (no priority tiers).
const sharedDeploymentLimit = (limits) => {
  const n = Number(limits?.deployments);
  return Number.isFinite(n) ? n : 0;
};

// Keys follow the snapshot's ``plans_available[].limits`` (current
// entity names: ``models`` = model definitions, ``pipelines`` =
// training pipelines). Every row except "Workspaces" is a
// PER-WORKSPACE allowance — each workspace on the plan gets that much.
const FEATURE_ROWS = [
  { key: 'workspaces',           label: 'Workspaces' },
  { key: 'segments',           label: 'Segments / workspace' },
  { key: 'models',             label: 'Models / workspace' },
  { key: 'pipelines',          label: 'Pipelines / workspace' },
  { key: 'goals',              label: 'Goals / workspace' },
  { key: 'predictfm_adapters', label: 'Foundation adapters / workspace' },
  { key: 'byom_models',        label: 'BYOM models / workspace' },
  { key: 'streams',            label: 'Event streams / workspace' },
  { key: 'signals',            label: 'Signals / workspace' },
  {
    key: '__shared_deployments__',
    label: 'Shared deployments / workspace',
    extract: sharedDeploymentLimit,
  },
  { key: 'dedicated_deployments', label: 'Dedicated deployments / workspace' },
];

const fmtLimit = (v) => {
  if (v == null) return '—';
  if (v === -1)  return 'Unlimited';
  if (typeof v === 'object') return JSON.stringify(v);
  return v.toLocaleString();
};

const fmtPrice = (usd) => {
  if (usd == null) return 'Contact sales';
  if (usd === 0)   return 'Free';
  return `$${Number(usd).toLocaleString()} / mo`;
};

// Lifecycle status of the current plan (from snapshot.plan.state).
const currentStatus = (current) => current?.state?.status || 'active';
const needsRenewal = (current) => ['expired', 'suspended', 'expiring_soon'].includes(currentStatus(current));

const PlanCard = ({
  plan, current, onSelect, busy, busyTargetId,
}) => {
  // Match on the SUBSCRIBED tier: an expired individual plan is priced
  // as Free, but the card the user recognises is the one they bought.
  const currentTier = (current?.subscribed_tier || current?.state?.tier || current?.name || '').toLowerCase();
  const isCurrent = currentTier === plan.id.toLowerCase()
    || currentTier === plan.name.toLowerCase();
  const status = currentStatus(current);
  const renewable = isCurrent && needsRenewal(current) && plan.id.toLowerCase() !== 'free';
  const movement = (() => {
    if (!currentTier) return 'switch';
    const a = tierRank(currentTier);
    const b = tierRank(plan.id);
    if (a === b) return renewable ? 'renew' : 'current';
    return b > a ? 'upgrade' : 'downgrade';
  })();
  const chipLabel = status === 'expired' || status === 'suspended' ? 'Expired'
    : status === 'expiring_soon' ? 'Ends soon' : 'Current plan';
  const chipColor = status === 'expired' || status === 'suspended' ? 'error'
    : status === 'expiring_soon' ? 'warning' : 'primary';

  const isBusy = busy && busyTargetId === plan.id;
  // Enterprise has a custom price → contact sales rather than a
  // self-serve checkout.
  const isCustomPriced = plan.monthly_fee_usd == null;

  return (
    <Card
      variant="outlined"
      sx={{
        flex: '1 1 240px',
        minWidth: 240,
        position: 'relative',
        borderColor: isCurrent ? `${chipColor}.main` : 'divider',
        borderWidth: isCurrent ? 2 : 1,
        bgcolor: isCurrent ? (theme) => theme.palette.action.hover : 'background.paper',
      }}
    >
      {isCurrent && (
        <Chip
          icon={<StarIcon sx={{ fontSize: 14 }} />}
          label={chipLabel}
          color={chipColor}
          size="small"
          sx={{ position: 'absolute', top: 12, right: 12 }}
        />
      )}
      <CardContent sx={{ p: 3 }}>
        <Typography variant="overline" color="text.secondary">
          {tierLabel(plan.id)}
        </Typography>
        <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
          {plan.name}
        </Typography>
        <Typography variant="h6" color="primary.main" sx={{ mb: 0.5 }}>
          {fmtPrice(plan.monthly_fee_usd)}
        </Typography>
        {plan.description && (
          <Typography variant="body2" color="text.secondary" sx={{ minHeight: 40 }}>
            {plan.description}
          </Typography>
        )}

        <Divider sx={{ mb: 1.5 }} />

        <Stack spacing={0.5} sx={{ mb: 2 }}>
          {plan.included_credits != null && (
            <Stack direction="row" alignItems="center" spacing={1}>
              <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="body2" sx={{ flex: 1 }}>
                Included credits
              </Typography>
              <Typography variant="body2" fontWeight={500}>
                {plan.included_credits.toLocaleString()}
              </Typography>
            </Stack>
          )}
          {FEATURE_ROWS.map((row) => {
            const value = row.extract
              ? row.extract(plan.limits || {})
              : (plan.limits || {})[row.key];
            // Hide the row when the plan doesn't expose the cap at all
            // (undefined) or - for derived caps like Shared deployments
            // - when the resolved cap is 0/undefined so we don't
            // pollute the comparison with empty rows.
            if (value === undefined) return null;
            if (row.extract && (value === 0 || value === null)) return null;
            return (
              <Stack key={row.key} direction="row" alignItems="center" spacing={1}>
                <CheckIcon sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {row.label}
                </Typography>
                <Typography variant="body2" fontWeight={500}>
                  {fmtLimit(value)}
                </Typography>
              </Stack>
            );
          })}
        </Stack>

        {isCurrent && !renewable ? (
          <Button fullWidth disabled variant="outlined">
            Active
          </Button>
        ) : isCurrent && renewable && isCustomPriced ? (
          <Button
            fullWidth
            variant="contained"
            color="error"
            href="mailto:sales@predict.ai?subject=Renew%20Enterprise%20plan"
          >
            Contact sales to renew
          </Button>
        ) : isCurrent && renewable ? (
          <Button
            fullWidth
            variant="contained"
            color={status === 'expiring_soon' ? 'warning' : 'error'}
            onClick={() => onSelect(plan, 'renew')}
            disabled={busy}
            startIcon={isBusy ? <CircularProgress size={14} color="inherit" /> : <RocketLaunchIcon fontSize="small" />}
          >
            {isBusy ? 'Redirecting…' : 'Renew plan'}
          </Button>
        ) : isCustomPriced ? (
          <Button
            fullWidth
            variant="outlined"
            href="mailto:sales@predict.ai?subject=Enterprise%20plan"
          >
            Contact sales
          </Button>
        ) : (
          <Button
            fullWidth
            variant={movement === 'upgrade' ? 'contained' : 'outlined'}
            color={movement === 'downgrade' ? 'inherit' : 'primary'}
            onClick={() => onSelect(plan, movement)}
            disabled={busy}
            startIcon={
              isBusy ? <CircularProgress size={14} color="inherit" /> :
              movement === 'upgrade' ? <RocketLaunchIcon fontSize="small" /> :
              movement === 'downgrade' ? <TrendingDownIcon fontSize="small" /> : null
            }
          >
            {isBusy
              ? 'Redirecting…'
              : movement === 'upgrade' ? 'Upgrade' :
                movement === 'downgrade' ? 'Downgrade' : 'Switch'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

const ConfirmDialog = ({
  open, plan, movement, onConfirm, onClose, busy, stripeConfigured, hasLiveSubscription,
}) => {
  if (!plan) return null;
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {movement === 'renew' ? 'Renew' : movement === 'upgrade' ? 'Upgrade to' : 'Switch to'} {plan.name}
      </DialogTitle>
      <DialogContent>
        {movement === 'renew' && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Renewing starts a new billing period for {plan.name} today. Read-only
            access is lifted and any paused goals, pipelines and deployments
            resume automatically as soon as the payment completes.
          </Alert>
        )}
        <Typography variant="body2" sx={{ mb: 2 }}>
          {stripeConfigured && hasLiveSubscription && plan.monthly_fee_usd == null
            ? `Your current subscription will end at the close of this billing period
               and you'll move to the ${plan.name} plan then. You keep everything
               you've paid for until that date; nothing further is charged.`
            : stripeConfigured && hasLiveSubscription
            ? `Your existing subscription is changed in place — no new checkout. The
               prorated difference is charged to your card on file right away, your
               new plan takes effect immediately and we'll grant the included monthly
               credits to your wallet.`
            : stripeConfigured
            ? `You'll be redirected to Stripe to complete checkout. After payment
               your new plan takes effect immediately and we'll grant the included
               monthly credits to your wallet.`
            : `Stripe isn't configured in this environment, so this change is applied
               immediately in simulation mode (no payment is collected). Your new plan
               takes effect right away and we'll grant the included monthly credits to
               your wallet.`}
        </Typography>
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">Monthly fee</Typography>
            <Typography variant="body2" fontWeight={600}>
              {fmtPrice(plan.monthly_fee_usd)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2" color="text.secondary">Included credits</Typography>
            <Typography variant="body2" fontWeight={600}>
              {plan.included_credits != null
                ? plan.included_credits.toLocaleString()
                : 'Custom'}
            </Typography>
          </Stack>
        </Box>
        {movement === 'downgrade' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Some features may become unavailable if you exceed the new plan's
            limits. Existing data is preserved but quota-blocked from creating
            new resources.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button
          variant="contained"
          color={movement === 'downgrade' ? 'warning' : 'primary'}
          onClick={onConfirm}
          disabled={busy}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {busy
            ? (stripeConfigured ? 'Redirecting…' : 'Applying…')
            : (stripeConfigured ? 'Continue to Stripe' : 'Confirm switch')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PlansPanel = ({
  snapshot,
  stripeConfigured,
  checkoutInFlight, checkoutError, checkoutResponse, hasLiveSubscription,
  doCheckout, doClearCheckout,
}) => {
  const [confirm, setConfirm] = useState(null); // { plan, movement }

  const plans = snapshot?.plans_available || [];
  const current = snapshot?.plan;

  // Sort by tier rank so the cards read left-to-right small-to-big.
  const sortedPlans = [...plans].sort((a, b) => tierRank(a.id) - tierRank(b.id));

  const handleConfirm = () => {
    if (!confirm) return;
    doCheckout({ mode: 'subscription', plan_tier: confirm.plan.id });
  };

  // The checkout saga redirects via window.location.assign on success;
  // if it errors, surface that and clear the in-flight state so the
  // user can retry.
  React.useEffect(() => {
    if (checkoutError) {
      doClearCheckout();
    }
  }, [checkoutError, doClearCheckout]);

  // Simulated checkout (Stripe not configured) applies the change
  // server-side and returns ``{applied: true}`` with no redirect URL.
  // Close the confirm dialog once that lands; the saga has already
  // refetched the snapshot so the current-plan card updates.
  React.useEffect(() => {
    if (checkoutResponse?.applied && !checkoutResponse?.url) {
      setConfirm(null);
      doClearCheckout();
    }
  }, [checkoutResponse, doClearCheckout]);

  const busy = checkoutInFlight;
  const errorMsg = checkoutError;

  return (
    <Box>
      <Typography variant="h6" fontWeight={600} sx={{ mb: 0.5 }}>
        Available plans
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Plans include a fixed monthly credit allowance; usage above the
        allowance is billed at the standard pay-as-you-go credit rate.
      </Typography>

      {!stripeConfigured && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Stripe Checkout isn't configured in this environment, so plan
          changes are applied immediately in simulation mode (no payment
          is collected). Switching takes effect right away.
        </Alert>
      )}

      {errorMsg && (
        <Alert severity="error" onClose={() => doClearCheckout()} sx={{ mb: 2 }}>
          {String(errorMsg)}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        {sortedPlans.map((plan) => (
          <PlanCard
            key={plan.id}
            plan={plan}
            current={current}
            onSelect={(p, m) => setConfirm({ plan: p, movement: m })}
            busy={busy}
            busyTargetId={confirm?.plan?.id}
          />
        ))}
      </Box>

      <ConfirmDialog
        open={Boolean(confirm)}
        plan={confirm?.plan}
        movement={confirm?.movement}
        busy={busy}
        stripeConfigured={stripeConfigured}
        hasLiveSubscription={hasLiveSubscription}
        onClose={() => !busy && setConfirm(null)}
        onConfirm={handleConfirm}
      />
    </Box>
  );
};

const mapStateToProps = (state) => ({
  stripeConfigured: state.billing_subscription?.response?.stripe_configured === true,
  // Owner already has a live Stripe subscription -> the backend changes
  // it in place (or schedules cancellation for Free) instead of opening
  // a second Checkout. Mirrors _LIVE_SUBSCRIPTION_STATUSES on the API.
  // ``managed_by === 'stripe'`` is only set for a REAL Stripe subscription
  // (never the simulated-checkout stub or an admin assignment), matching
  // the API's own change-in-place check.
  hasLiveSubscription: state.billing_subscription?.response?.managed_by === 'stripe'
    && ['active', 'trialing', 'past_due'].includes(
      state.billing_subscription?.response?.subscription_status,
    ),
  checkoutInFlight: state.billing_checkout?.isLoading,
  checkoutError:    state.billing_checkout?.error,
  checkoutResponse: state.billing_checkout?.response,
});

const mapDispatchToProps = {
  doCheckout:       startCheckout,
  doClearCheckout:  clearCheckoutState,
};

export default connect(mapStateToProps, mapDispatchToProps)(PlansPanel);
